import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import {
  X, User, Save, Trash2, Search,
  Briefcase, Clock, Upload, Eye, PlusCircle, ArrowDownAZ,
  GripVertical, Layers, Edit2, Plus, ChevronRight, ChevronLeft, FileText,
  MapPin, Calendar, Users, Info, Scissors, Camera
} from 'lucide-react';

import { pb, getFileUrl } from '../../lib/pb';
import { calcBusinessHours, calcWorkingDays, addBusinessHours, compressImage, getTimelineSegments, pdfToImage } from '../../lib/utils';
import RangeDatePicker from '../../components/admin/RangeDatePicker';
import ChantierModal from '../../components/admin/ChantierModal';
import TravelIndicator from '../../components/admin/TravelIndicator';
import { useData } from '../../context/DataContext';
import '../../styles/admin.css';

// --- MINI CALENDAR COMPONENT FOR FORM ---
const MiniCalendar = ({ value, onChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(value || new Date()));
  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0

  const month = currentMonth.getMonth();
  const year = currentMonth.getFullYear();
  const days = daysInMonth(month, year);
  const startDay = firstDayOfMonth(month, year);

  const selectedDate = value ? new Date(value).toISOString().split('T')[0] : '';

  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));
  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));

  const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  return (
    <div style={{ background: '#fff', borderRadius: 20, border: '1.5px solid #e2e8f0', padding: 16, userSelect: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
        <button type="button" onClick={prevMonth} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>←</button>
        <span style={{ fontWeight: 900, fontSize: 13, textTransform: 'capitalize', color: '#0f172a' }}>
          {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" onClick={nextMonth} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>→</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5 }}>
        {dayNames.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 900, color: '#94a3b8', paddingBottom: 5 }}>{d}</div>
        ))}
        {Array.from({ length: startDay }).map((_, i) => <div key={`empty-${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = selectedDate === dateStr;
          const isToday = new Date().toISOString().split('T')[0] === dateStr;

          return (
            <div
              key={day}
              onClick={() => onChange(dateStr)}
              style={{
                textAlign: 'center', padding: '8px 0', fontSize: 13, fontWeight: 800, borderRadius: 10, cursor: 'pointer',
                background: isSelected ? '#3b82f6' : 'transparent',
                color: isSelected ? '#fff' : (isToday ? '#3b82f6' : '#334155'),
                border: isToday && !isSelected ? '1.5px solid #3b82f6' : 'none',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => !isSelected && (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={e => !isSelected && (e.currentTarget.style.background = 'transparent')}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Common UI utilities moved to lib/utils.js ---
const WORKER_COLORS = [
  '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#f43f5e', '#6366f1', '#14b8a6', '#f97316', '#2dd4bf'
];

function getWorkerColor(user) {
  if (!user) return '#cbd5e1';
  if (user.color) return user.color;

  // Deterministic color selection based on ID as fallback
  const id = user.id || '';
  let hash = 0;
  for (let i = 0; i < id.length; i++) { hash = id.charCodeAt(i) + ((hash << 5) - hash); }
  return WORKER_COLORS[Math.abs(hash) % WORKER_COLORS.length];
}

function renderEventContent(eventInfo) {
  const { event, view } = eventInfo;
  const users = event.extendedProps.users || [];
  const status = event.extendedProps.status;
  const isActive = event.extendedProps.active;
  const type = event.extendedProps.type || 'mission';
  const viewType = view.type;

  const isRDV = type === 'rdv';
  const rdvColor = '#a855f7'; // Purple Pearl/Amethyst

  // Calculate hours for THIS segment only
  const segHours = calcBusinessHours(event.start, event.end, event.extendedProps.businessHours, event.extendedProps.closures) || 0;
  const totalSegHours = Math.round(segHours * (users.length || 1));
  const missionH = event.extendedProps.missionH || 0;
  const perPersonTotalH = Math.round(missionH / (users.length || 1));

  const customColor = isRDV ? rdvColor : event.extendedProps.chantier?.color;

  return (
    <div className={`unified-event-card status-${status} ${!isActive ? 'striped-inactive' : ''} ${isRDV ? 'is-rdv-item' : ''}`}
      style={{
        position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        backgroundColor: isActive && customColor ? customColor : undefined,
        borderLeftColor: customColor || undefined,
        color: isActive && customColor ? '#fff' : '#0f172a',
        boxShadow: isRDV ? '0 4px 10px rgba(168, 85, 247, 0.2)' : undefined,
        paddingLeft: 22
      }}>

      <button
        onClick={(e) => {
          e.stopPropagation();
          if (window.openMissionDetail) window.openMissionDetail(event);
        }}
        style={{
          position: 'absolute', top: 3, left: 3, padding: 3, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'inherit',
          transition: 'all 0.2s', zIndex: 10
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        title="Voir détails"
      >
        <Eye size={12} />
      </button>

      <div className="unified-title" style={{ opacity: isActive ? 1 : 0.6, padding: '2px 4px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isActive ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isRDV && <Calendar size={10} style={{ opacity: 0.8 }} />}
              <strong style={{ fontSize: viewType === 'dayGridMonth' ? '9px' : '11px', lineHeight: 1.1, paddingRight: 20 }}>
                {event.title}
              </strong>
            </div>

            {!isRDV && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', marginTop: 2 }}>
                {viewType === 'dayGridMonth' ? (
                  <span style={{ opacity: 0.9, fontSize: '8px', fontWeight: 900 }}>{missionH}h ({users.length}p: {users.map(u => (u.name || u.username || '?').split(' ')[0]).join(',')})</span>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ opacity: 0.9, fontSize: '8px', fontWeight: 900, background: 'rgba(255,255,255,0.15)', padding: '1px 4px', borderRadius: 4 }}>
                        {event.start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {event.end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{ opacity: 0.9, fontSize: '10px', fontWeight: 900 }}>{totalSegHours}h / {missionH}h T. ({users.length} pers: {users.map(u => (u.name || u.username || '?').split(' ')[0]).join(', ')})</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {isRDV && viewType !== 'dayGridMonth' && (
              <span style={{ opacity: 0.8, fontSize: '9px', fontWeight: 900 }}>
                {event.start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {event.end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </>
        ) : (
          <span style={{ fontSize: '8px', fontStyle: 'italic' }}>{event.title} (OFF)</span>
        )}
      </div>
      {isActive && users.length > 0 && (
        <div className="worker-rainbow-strip" style={{ background: customColor ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.03)' }}>
          {users.map((u, i) => (
            <div key={i} 
              className="worker-dot" 
              onClick={(e) => { e.stopPropagation(); if (window.openMemberProfile) window.openMemberProfile(u); }}
              style={{ background: getWorkerColor(u), cursor: 'pointer' }} 
              title={u.name} 
            ></div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPlanning() {
  const { chantiers, users: workers, assignments: rawAssignments, settings: cfg, loading: globalLoading, refresh } = useData();
  const [sidebarSort, setSidebarSort] = useState('latest'); // 'latest' or 'alpha'
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false); // Local loading for actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalTab, setModalTab] = useState('details');
  const [isQuickCreatingChantier, setIsQuickCreatingChantier] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [formData, setFormData] = useState({ id: '', chantier: '', users: [], start_date: '', end_date: '', notes: '', hours_override: '', type: 'mission', title: '', split_budget: false, coupure_midi: false });
  const [editingChantier, setEditingChantier] = useState(null);
  const [isChantierModalOpen, setIsChantierModalOpen] = useState(false);
  const [siteDetails, setSiteDetails] = useState({ title: '', client_name: '', location: '', status: 'planned', description: '', attachments: [], gallery: [] });
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const externalRef = useRef(null);
  const calendarRef = useRef(null);

  const businessHours = cfg?.business_hours;
  const closures = cfg?.exceptional_closures || [];

  const { slotMin, slotMax } = useMemo(() => {
    if (!businessHours) return { slotMin: '08:00:00', slotMax: '18:00:00' };
    let minH = 23; let minM = 59;
    let maxH = 0; let maxM = 0;
    let found = false;

    Object.values(businessHours).forEach(bh => {
      if (bh.active && bh.open && bh.close) {
        found = true;
        const [oh, om] = bh.open.split(':').map(Number);
        const [ch, cm] = bh.close.split(':').map(Number);
        if (oh < minH || (oh === minH && om < minM)) { minH = oh; minM = om; }
        if (ch > maxH || (ch === maxH && cm > maxM)) { maxH = ch; maxM = cm; }
      }
    });

    if (!found) return { slotMin: '08:00:00', slotMax: '18:30:00' };

    return {
      slotMin: `${minH.toString().padStart(2, '0')}:${minM.toString().padStart(2, '0')}:00`,
      slotMax: `${maxH.toString().padStart(2, '0')}:${maxM.toString().padStart(2, '0')}:00`
    };
  }, [businessHours]);

  const [viewType, setViewType] = useState('dayGridMonth');
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, event: null });
  const [isDragging, setIsDragging] = useState(false);
  const [isCutterMode, setIsCutterMode] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);

  // Global bridge for renderEventContent icons
  useEffect(() => {
    window.openMissionDetail = (event) => {
      setCardData(event);
      setIsCardOpen(true);
    };
    window.openMemberProfile = (member) => {
      setSelectedMember(member);
      setIsMemberModalOpen(true);
    };
    return () => { 
      delete window.openMissionDetail; 
      delete window.openMemberProfile;
    };
  }, []);

  const handleEventClick = (info) => {
    const raw = info.event.extendedProps.raw;
    if (!raw) return;
    setModalMode('edit');

    // Ensure users is always an array of IDs
    let userIds = [];
    if (Array.isArray(raw.user)) {
      userIds = raw.user;
    } else if (typeof raw.user === 'string' && raw.user) {
      userIds = [raw.user];
    } else if (raw.user?.id) {
      userIds = [raw.user.id];
    }

    setFormData({
      id: raw.id,
      rawIds: info.event.extendedProps.rawIds || [raw.id],
      chantier: raw.chantier || '',
      users: userIds,
      start_date: info.event.startStr.slice(0, 16),
      end_date: (info.event.endStr || info.event.startStr).slice(0, 16),
      notes: raw.notes || '',
      hours_override: raw.hours_override || '',
      type: raw.type || 'mission',
      title: raw.title || '',
      coupure_midi: raw.coupure_midi || false,
      sessions: []
    });
    setIsModalOpen(true);
  };

  const handleDeleteAssignment = async (idOrIds) => {
    if (!window.confirm('Supprimer cette planification ?')) return;
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    try {
      await Promise.all(ids.map(id => pb.collection('assignments').delete(id)));
      setIsCardOpen(false);
      refresh();
    } catch (e) { alert('Erreur suppression'); }
  };

  const handleDuplicateAssignment = async (event) => {
    try {
      const data = { ...event.extendedProps.raw, id: undefined };
      await pb.collection('assignments').create(data);
      refresh();
    } catch (e) { alert('Erreur duplication'); }
  };

  const handleSplitAssignment = async (event) => {
    try {
      const raw = event.extendedProps.raw;
      const rawIds = event.extendedProps.rawIds || [raw.id];
      if (!raw) return;

      const start = new Date(event.start);
      const end = new Date(event.end);
      const midTime = start.getTime() + (end.getTime() - start.getTime()) / 2;
      const midDate = new Date(midTime).toISOString();

      for (const aid of rawIds) {
        const a = rawAssignments.find(x => x.id === aid);
        if (!a) continue;

        const hA = calcBusinessHours(a.start_date, midDate, businessHours, closures);
        const hB = calcBusinessHours(midDate, a.end_date, businessHours, closures);

        await pb.collection('assignments').update(aid, { end_date: midDate, hours_override: hA || null });
        await pb.collection('assignments').create({ ...a, id: undefined, start_date: midDate, hours_override: hB || null });
      }
      refresh();
    } catch (e) { alert('Erreur division'); }
  };

  const handleDetachDay = async (event) => {
    try {
      const raw = event.extendedProps.raw;
      const rawIds = event.extendedProps.rawIds || [raw.id];
      if (!raw) return;

      const segStart = new Date(event.start).toISOString();
      const segEnd = new Date(event.end).toISOString();
      const origStart = new Date(raw.start_date).toISOString();
      const origEnd = new Date(raw.end_date).toISOString();

      for (const aid of rawIds) {
        const a = rawAssignments.find(x => x.id === aid);
        if (!a) continue;

        // 1. Pre-Segment
        if (origStart < segStart) {
          const h = calcBusinessHours(origStart, segStart, businessHours, closures);
          await pb.collection('assignments').create({ ...a, id: undefined, end_date: segStart, hours_override: h || null });
        }
        // 2. The Day
        const hDay = calcBusinessHours(segStart, segEnd, businessHours, closures);
        await pb.collection('assignments').create({ ...a, id: undefined, start_date: segStart, end_date: segEnd, hours_override: hDay || null });

        // 3. Post-Segment
        if (origEnd > segEnd) {
          const h = calcBusinessHours(segEnd, origEnd, businessHours, closures);
          await pb.collection('assignments').create({ ...a, id: undefined, start_date: segEnd, hours_override: h || null });
        }
        await pb.collection('assignments').delete(aid);
      }
      refresh();
    } catch (e) { alert('Erreur détachement'); }
  };
  // Generate events whenever raw data changes or view changes
  useEffect(() => {
    if (globalLoading) return;
    
    // 1. FILTER assignments by selected workers if any
    const filteredRaw = selectedWorkerIds.length === 0 
      ? rawAssignments 
      : rawAssignments.filter(a => {
          const uIds = Array.isArray(a.user) ? a.user : (a.user ? [a.user] : []);
          return uIds.some(uid => selectedWorkerIds.includes(uid));
      });

    // 2. Group by visual common slots (Start+End+Site)
    const unified = [];
    const groups = {};
    filteredRaw.forEach(a => {
      const type = a.type || 'mission';
      const key = type === 'rdv'
        ? `rdv-${a.title}-${a.start_date}-${a.end_date}`
        : `mission-${a.chantier}-${a.start_date}-${a.end_date}`;

      if (!groups[key]) {
        groups[key] = {
          id: a.id,
          groupId: key,
          chantierId: a.chantier,
          start: a.start_date,
          end: a.end_date,
          title: type === 'rdv' ? (a.title || 'RDV') : (a.expand?.chantier?.title || 'Chantier'),
          type: type,
          status: type === 'rdv' ? 'active' : a.expand?.chantier?.status,
          assignments: [],
          chantier: a.expand?.chantier
        };
        unified.push(groups[key]);
      }
      groups[key].assignments.push(a);
    });

    const finalEvents = [];
    unified.forEach(g => {
      const totalH = calcBusinessHours(g.start, g.end, businessHours, closures) || 0;
      const combinedMissionH = g.assignments.reduce((sum, a) => {
        const uCount = Array.isArray(a.user) ? a.user.length : (a.user ? 1 : 0);
        const h = a.hours_override != null ? parseFloat(a.hours_override) : totalH;
        return sum + (h * uCount);
      }, 0);

      // SMART SEGMENT MERGING for ALL views
      const rawSegments = getTimelineSegments(g.start, g.end, businessHours, closures);
      const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const mergedSegments = [];
      let currentSeg = null;

      rawSegments.forEach(seg => {
        const d = new Date(seg.start);
        const dateStr = d.toISOString().split('T')[0];
        const dayName = DAY_KEYS[d.getDay()];
        const isFullClosure = businessHours?.[dayName]?.active === false ||
          (closures || []).some(c => c.date === dateStr);

        if (isFullClosure) {
          if (currentSeg) { mergedSegments.push(currentSeg); currentSeg = null; }
          return;
        }

        // If it's a working day, we merge everything (Day + Night) to keep it linear
        if (!currentSeg) {
          currentSeg = { ...seg };
        } else {
          currentSeg.end = seg.end;
        }
      });
      if (currentSeg) mergedSegments.push(currentSeg);

      mergedSegments.forEach((seg, idx) => {
        finalEvents.push({
          id: `${g.id}-${idx}`,
          groupId: g.groupId,
          title: g.title,
          start: seg.start,
          end: seg.end,
          extendedProps: {
            active: true,
            type: g.type,
            missionH: combinedMissionH,
            chantier: g.chantier,
            users: g.assignments.flatMap(a => {
              const u = a.expand?.user;
              return Array.isArray(u) ? u : (u ? [u] : []);
            }).filter(Boolean),
            status: g.status,
            businessHours: businessHours,
            closures: closures,
            rawIds: g.assignments.map(a => a.id),
            raw: g.assignments[0]
          }
        });
      });
    });
    setEvents(finalEvents);
  }, [rawAssignments, businessHours, closures, viewType, selectedWorkerIds]);

  const dayTotals = useMemo(() => {
    const totals = {};
    events.forEach(evt => {
      if (evt.extendedProps?.active) {
        const d = evt.start.split('T')[0];
        const h = calcBusinessHours(evt.start, evt.end, businessHours, closures);
        totals[d] = (totals[d] || 0) + (h * (evt.extendedProps.users?.length || 1));
      }
    });
    return totals;
  }, [events, businessHours, closures]);

  // --- SMART AUTO-CALC END DATE ---
  const prevStart = useRef(formData.start_date);
  const prevHours = useRef(formData.hours_override);
  const prevEnd = useRef(formData.end_date);

  useEffect(() => {
    const startChanged = prevStart.current !== formData.start_date;
    const hoursChanged = prevHours.current !== formData.hours_override;
    const endChanged = prevEnd.current !== formData.end_date;

    // Only auto-calc end_date if hours or start changed, and it wasn't an explicit end_date change
    if ((startChanged || hoursChanged) && !endChanged && formData.start_date && formData.hours_override && businessHours) {
      const h = parseFloat(formData.hours_override);
      if (!isNaN(h) && h > 0) {
        const newEnd = addBusinessHours(formData.start_date, h, businessHours, closures);
        if (newEnd && newEnd.slice(0, 16) !== formData.end_date?.slice(0, 16)) {
          setFormData(prev => ({ ...prev, end_date: newEnd.slice(0, 16) }));
          prevEnd.current = newEnd.slice(0, 16);
        }
      }
    }

    prevStart.current = formData.start_date;
    prevHours.current = formData.hours_override;
    prevEnd.current = formData.end_date;
  }, [formData.start_date, formData.end_date, formData.hours_override, businessHours, closures]);

  useEffect(() => {
    if (formData.chantier && !isQuickCreatingChantier) {
      const c = chantiers.find(x => x.id === formData.chantier);
      if (c) {
        setSiteDetails(c);
        if (modalMode === 'create') {
          const cAs = rawAssignments.filter(a => a.chantier === c.id);
          const already = cAs.reduce((acc, a) => acc + (a.hours_override || calcBusinessHours(a.start_date, a.end_date, businessHours, closures) || 0), 0);
          const budget = c.budget_hours || 0;
          const remainingRaw = budget - already;
          const remaining = Math.round(Math.max(0, remainingRaw));

          let nextEnd = formData.end_date;
          if (remaining > 0 && formData.start_date && businessHours) {
            nextEnd = addBusinessHours(formData.start_date, remaining, businessHours, closures).slice(0, 16);
          }

          setFormData(prev => ({
            ...prev,
            notes: prev.notes || c.description || '',
            hours_override: remaining > 0 ? remaining : (prev.hours_override || ''),
            end_date: nextEnd
          }));
        }
      }
    }
  }, [formData.chantier, chantiers, isQuickCreatingChantier, modalMode, rawAssignments, businessHours, closures]);

  // --- Init FullCalendar external draggable ---
  useEffect(() => {
    let draggable = null;
    if (externalRef.current) {
      draggable = new Draggable(externalRef.current, {
        itemSelector: '.fc-external-event',
        eventData: (el) => ({
          title: el.getAttribute('data-title'),
          extendedProps: {
            chantierId: el.getAttribute('data-id'),
            hours: parseFloat(el.getAttribute('data-hours')) || 8
          }
        })
      });
    }
    return () => { if (draggable) draggable.destroy(); };
  }, [chantiers, sidebarOpen]);

  const handleFileUpload = async (e, field) => {
    const files = e.target.files; if (!files || files.length === 0 || !formData.chantier) return;
    setLoading(true); setUploadError('');
    try {
      for (const f of files) {
        if (f.type === 'application/pdf') {
          const imgs = await pdfToImage(f);
          for (const img of (Array.isArray(imgs) ? imgs : [imgs])) {
            const fd = new FormData(); fd.append(field + '+', img);
            const updated = await pb.collection('chantiers').update(formData.chantier, fd);
            setSiteDetails(updated);
            refresh();
          }
        } else {
          const opt = f.type.startsWith('image/') ? await compressImage(f) : f;
          const fd = new FormData();
          fd.append(field + '+', opt);
          const updated = await pb.collection('chantiers').update(formData.chantier, fd);
          setSiteDetails(updated);
          refresh();
        }
      }
    } catch (err) { setUploadError("Erreur d'upload."); } finally { setLoading(false); }
  };

  const handleRemoveFile = async (fileName, field) => {
    if (!window.confirm("Supprimer ?")) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append(field + '-', fileName);
      const updated = await pb.collection('chantiers').update(formData.chantier, fd);
      setSiteDetails(updated);
      refresh();
    } catch (err) { } finally { setLoading(false); }
  };

  const handleEventDrop = async (info) => {
    const { event, oldEvent } = info;
    const raw = event.extendedProps.raw;
    const rawIds = event.extendedProps.rawIds || [raw?.id];
    if (!rawIds.length || !rawIds[0]) return;

    try {
      const { businessHours, closures } = event.extendedProps;

      // 1. Find the REAL total range of the group in the assignments list
      const groupAssignments = rawAssignments.filter(a => rawIds.includes(a.id));
      if (!groupAssignments.length) return;

      const minStart = groupAssignments.reduce((acc, a) => a.start_date < acc ? a.start_date : acc, groupAssignments[0].start_date);
      const maxEnd = groupAssignments.reduce((acc, a) => a.end_date > acc ? a.end_date : acc, groupAssignments[0].end_date);

      // 2. Calculate TOTAL work hours for the whole group
      const totalHours = calcBusinessHours(minStart, maxEnd, businessHours, closures) || 8;

      // 3. New Start is where we dropped it MINUS the offset relative to the minStart
      // Actually, simplest is to just use the drop start as the new beginning of the entire mission
      const newStartLocal = event.start.toISOString().slice(0, 16);
      const newEndLocal = addBusinessHours(newStartLocal, totalHours, businessHours, closures);

      const dbStart = new Date(newStartLocal).toISOString();
      const dbEnd = new Date(newEndLocal).toISOString();

      for (const aid of rawIds) {
        await pb.collection('assignments').update(aid, {
          start_date: dbStart,
          end_date: dbEnd
        });
      }
      refresh();
    } catch (e) {
      info.revert();
    } finally {
      setLoading(false);
    }
  };

  const handleEventResize = async (info) => {
    const { event } = info;
    const raw = event.extendedProps.raw;
    const rawIds = event.extendedProps.rawIds || [raw?.id];
    if (!rawIds.length || !rawIds[0]) return;

    setLoading(true);
    try {
      const toLocalISO = (date) => {
        const d = new Date(date);
        return d.getFullYear() + "-" +
          String(d.getMonth() + 1).padStart(2, '0') + "-" +
          String(d.getDate()).padStart(2, '0') + "T" +
          String(d.getHours()).padStart(2, '0') + ":" +
          String(d.getMinutes()).padStart(2, '0');
      };

      const startLocal = toLocalISO(event.start);
      const endLocal = toLocalISO(event.end);
      const newHours = calcBusinessHours(startLocal, endLocal, businessHours, closures);

      const dbStart = new Date(startLocal).toISOString();
      const dbEnd = new Date(endLocal).toISOString();

      for (const aid of rawIds) {
        await pb.collection('assignments').update(aid, {
          start_date: dbStart,
          end_date: dbEnd,
          hours_override: newHours
        });
      }
      refresh();
    } catch (e) {
      info.revert();
    } finally {
      setLoading(false);
    }
  };

  // --- MOBILE DETECTION ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [mobileSelectedDate, setMobileSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [mobileView, setMobileView] = useState('week');
  const [mobileMonth, setMobileMonth] = useState(() => new Date());


  useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', sync); return () => window.removeEventListener('resize', sync);
  }, []);

  return (
    <div className="planning-page">
      {globalLoading ? (
        <div className="skeleton-container">
          <div className="skeleton-header"></div>
          <div className="skeleton-body">
            <div className="skeleton-sidebar"></div>
            <div className="skeleton-calendar"></div>
          </div>
        </div>
      ) : (
        <>
          <header className="planning-header" style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 0, paddingBottom: isMobile ? 10 : 0 }}>
            <div className="header-text" style={{ maxWidth: '100%' }}>
              <h1 style={isMobile ? { fontSize: 22, margin: 0, paddingBottom: 4 } : {}}>Planning & Équipes</h1>
              {!isMobile && <p className="muted">Gérez les interventions, les transports et les plannings salariés en temps réel.</p>}
            </div>
            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: isMobile ? 'auto' : 'visible',
              width: isMobile ? '100%' : 'auto',
              maxWidth: '100%',
              paddingBottom: isMobile ? 4 : 0,
              scrollbarWidth: 'none',
              boxSizing: 'border-box'
            }} className="w-media-scroll">
              {!isMobile && (
                <button
                  className="btn-add-main"
                  style={{ background: sidebarOpen ? '#f0fdf4' : '#f8fafc', color: sidebarOpen ? '#16a34a' : '#64748b', border: '1.5px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <Layers size={18} />
                  <span>{sidebarOpen ? 'Masquer Sidebar' : 'Afficher Sidebar'}</span>
                </button>
              )}
              {!isMobile && (
                <button
                  className={`btn-add-main ${isCutterMode ? 'cutter-active' : ''}`}
                  onClick={() => setIsCutterMode(!isCutterMode)}
                  style={{
                    background: isCutterMode ? '#ef4444' : '#fff',
                    color: isCutterMode ? '#fff' : '#475569',
                    border: '1.5px solid ' + (isCutterMode ? '#ef4444' : '#e2e8f0'),
                    marginRight: 10,
                    flexShrink: 0
                  }}
                >
                  <Scissors size={18} />
                  <span>{isCutterMode ? 'Mode Découpe ACTIF' : 'Mode Découpe'}</span>
                </button>
              )}
              <button className="btn-add-main"
                style={{ background: '#f5f3ff', color: '#7c3aed', border: '1.5px solid #ddd6fe', boxShadow: 'none', flexShrink: 0, padding: isMobile ? '8px 12px' : '' }}
                onClick={() => {
                  setModalMode('create');
                  setFormData({ id: '', chantier: '', users: [], start_date: new Date().toISOString().slice(0, 16), end_date: '', notes: '', hours_override: '', type: 'rdv', title: '', coupure_midi: false });
                  setIsModalOpen(true);
                }}>
                <Calendar size={18} />
                <span style={{ fontSize: isMobile ? 12 : 14 }}>Nouveau RDV</span>
              </button>
              <button className="btn-add-main" style={{ flexShrink: 0, padding: isMobile ? '8px 12px' : '' }} onClick={() => {
                setModalMode('create');
                setFormData({ id: '', chantier: '', users: [], start_date: new Date().toISOString().slice(0, 16), end_date: '', notes: '', hours_override: '', type: 'mission', title: '', split_budget: false, coupure_midi: false });
                setIsModalOpen(true);
              }}>
                <PlusCircle size={18} />
                <span style={{ fontSize: isMobile ? 12 : 14 }}>Nouvelle Mission</span>
              </button>
            </div>
          </header>

          <main className={`planning-main ${isCutterMode ? 'cutter-mode-active' : ''}`}>
            {loading && (
              <div className="loading-state-overlay">
                <Clock className="spin" size={32} />
                <p>Mise à jour en cours...</p>
              </div>
            )}

            {isMobile ? (
              <div className="mobile-timeline" style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0', paddingBottom: 100, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
                {/* Mobile View Toggle */}
                <div style={{ padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#0f172a' }}>Mon Planning</div>
                  <div className="w-view-switch" style={{ background: '#f1f5f9', padding: 4, borderRadius: 10, display: 'flex', gap: 4 }}>
                    <button style={{ border: 'none', padding: '6px 12px', fontSize: 11, fontWeight: 800, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s', background: mobileView === 'week' ? '#fff' : 'transparent', color: mobileView === 'week' ? '#3b82f6' : '#64748b', boxShadow: mobileView === 'week' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }} onClick={() => setMobileView('week')}>Sem.</button>
                    <button style={{ border: 'none', padding: '6px 12px', fontSize: 11, fontWeight: 800, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s', background: mobileView === 'month' ? '#fff' : 'transparent', color: mobileView === 'month' ? '#3b82f6' : '#64748b', boxShadow: mobileView === 'month' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }} onClick={() => { setMobileView('month'); setMobileMonth(new Date(mobileSelectedDate)); }}>Mois</button>
                  </div>
                </div>

                {mobileView === 'week' && (
                  <div style={{ position: 'sticky', top: 0, zIndex: 50, display: 'flex', overflowX: 'auto', padding: '12px 10px', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e2e8f0', gap: 10, scrollbarWidth: 'none', width: '100%', boxSizing: 'border-box' }} className="w-media-scroll">
                    {Array.from({ length: 15 }).map((_, i) => {
                      const d = new Date();
                      d.setDate(d.getDate() - 3 + i);
                      const dStr = d.toISOString().split('T')[0];
                      const isSelected = dStr === mobileSelectedDate;
                      const dayNamesShort = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

                      const hasAs = events.some(e => e.extendedProps.active && dStr >= e.start.split('T')[0] && dStr <= e.end.split('T')[0]);

                      return (
                        <div
                          key={dStr}
                          onClick={() => setMobileSelectedDate(dStr)}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto',
                            minWidth: 50, height: 60, borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0, position: 'relative',
                            background: isSelected ? 'var(--w-accent-dark)' : 'transparent',
                            color: isSelected ? '#fff' : '#64748b',
                            border: isSelected ? 'none' : '1px solid #e2e8f0'
                          }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{dayNamesShort[d.getDay()]}</span>
                          <span style={{ fontSize: 18, fontWeight: 900 }}>{d.getDate()}</span>
                          {hasAs && !isSelected && <div style={{ width: 4, height: 4, background: '#3b82f6', borderRadius: '50%', position: 'absolute', bottom: 6 }} />}
                          {hasAs && isSelected && <div style={{ width: 4, height: 4, background: '#fff', borderRadius: '50%', position: 'absolute', bottom: 6 }} />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {mobileView === 'month' && (
                  <div style={{ background: '#fff', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                      <button type="button" onClick={() => setMobileMonth(new Date(mobileMonth.getFullYear(), mobileMonth.getMonth() - 1, 1))} style={{ border: 'none', background: '#f1f5f9', width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}><ChevronLeft size={16} /></button>
                      <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'capitalize', color: '#0f172a' }}>
                        {mobileMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </span>
                      <button type="button" onClick={() => setMobileMonth(new Date(mobileMonth.getFullYear(), mobileMonth.getMonth() + 1, 1))} style={{ border: 'none', background: '#f1f5f9', width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}><ChevronRight size={16} /></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 900, color: '#94a3b8', paddingBottom: 8 }}>{d}</div>)}
                      {(() => {
                        const y = mobileMonth.getFullYear();
                        const m = mobileMonth.getMonth();
                        const daysInM = new Date(y, m + 1, 0).getDate();
                        let startDay = new Date(y, m, 1).getDay();
                        startDay = startDay === 0 ? 6 : startDay - 1;

                        const cells = [];
                        for (let i = 0; i < startDay; i++) cells.push(<div key={`empty-${i}`} />);

                        for (let i = 1; i <= daysInM; i++) {
                          const iterD = new Date(y, m, i);
                          const dStr = iterD.toISOString().split('T')[0];
                          const iterToday = new Date().toISOString().split('T')[0] === dStr;
                          const isSelected = dStr === mobileSelectedDate;
                          const hasAs = events.some(e => e.extendedProps.active && dStr >= e.start.split('T')[0] && dStr <= e.end.split('T')[0]);

                          cells.push(
                            <div
                              key={`day-${i}`}
                              onClick={() => { setMobileSelectedDate(dStr); setMobileView('week'); }}
                              style={{ height: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer', borderRadius: 10, fontSize: 12, fontWeight: 800, background: isSelected ? '#3b82f6' : 'transparent', color: isSelected ? '#fff' : (iterToday ? '#3b82f6' : '#475569'), border: iterToday && !isSelected ? '1.5px solid #3b82f6' : 'none' }}
                            >
                              {i}
                              {hasAs && !isSelected && <div style={{ width: 4, height: 4, background: '#3b82f6', borderRadius: '50%', position: 'absolute', bottom: 4 }} />}
                              {hasAs && isSelected && <div style={{ width: 4, height: 4, background: '#fff', borderRadius: '50%', position: 'absolute', bottom: 4 }} />}
                            </div>
                          );
                        }
                        return cells;
                      })()}
                    </div>
                  </div>
                )}

                {/* Vertical Timeline List */}
                <div style={{ padding: '16px' }}>
                  {events.filter(e => {
                    if (!e.extendedProps.active) return false;
                    const eStart = e.start.split('T')[0];
                    const eEnd = e.end.split('T')[0];
                    return mobileSelectedDate >= eStart && mobileSelectedDate <= eEnd;
                  }).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Aucune mission prévue pour ce jour.</div>
                  ) : (
                    events.filter(e => {
                      if (!e.extendedProps.active) return false;
                      const eStart = e.start.split('T')[0];
                      const eEnd = e.end.split('T')[0];
                      return mobileSelectedDate >= eStart && mobileSelectedDate <= eEnd;
                    }).sort((a, b) => new Date(a.start) - new Date(b.start)).map((evt, idx) => {
                      const isRDV = evt.extendedProps.type === 'rdv';
                      const customColor = isRDV ? '#a855f7' : (evt.extendedProps.chantier?.color || '#0ea5e9');

                      return (
                        <div key={evt.id} style={{
                          background: '#fff', borderRadius: 16, padding: '14px 16px', marginBottom: 12,
                          borderLeft: `6px solid ${customColor}`,
                          boxShadow: '0 4px 10px rgba(0,0,0,0.03)', position: 'relative', cursor: 'pointer'
                        }} onClick={() => window.openMissionDetail ? window.openMissionDetail(evt) : null}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>
                                {new Date(evt.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(evt.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 900, color: '#0f172a' }}>{evt.title}</div>
                            </div>
                            <div style={{ background: isRDV ? '#f3e8ff' : '#e0f2fe', color: customColor, fontSize: 9, fontWeight: 900, padding: '4px 8px', borderRadius: 8, textTransform: 'uppercase' }}>
                              {isRDV ? 'RDV' : 'CHANTIER'}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
                            <div style={{ display: 'flex', marginRight: 10 }}>
                              {evt.extendedProps.users?.length > 0 ? evt.extendedProps.users.map((u, i) => (
                                <div key={i} style={{ width: 24, height: 24, borderRadius: '50%', background: getWorkerColor(u), border: '2px solid #fff', marginLeft: i > 0 ? -8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 800 }} title={u.name}>
                                  {u.name ? u.name.charAt(0).toUpperCase() : '?'}
                                </div>
                              )) : (
                                <div style={{ fontSize: 11, fontWeight: 700, fontStyle: 'italic', color: '#94a3b8' }}>Non assigné</div>
                              )}
                            </div>

                            {evt.extendedProps.chantier?.status && !isRDV && (
                              <span style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8' }}>{evt.extendedProps.chantier.status}</span>
                            )}
                          </div>

                          <ChevronRight size={16} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1' }} />
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Floating Quick Action */}
                <button
                  onClick={() => {
                    setModalMode('create');
                    setFormData({ id: '', chantier: '', users: [], start_date: new Date().toISOString().slice(0, 16), end_date: '', notes: '', hours_override: '', type: 'mission', title: '' });
                    setIsModalOpen(true);
                  }}
                  style={{ position: 'fixed', bottom: 100, right: 16, width: 64, height: 64, borderRadius: '50%', background: 'var(--w-accent-dark)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 25px rgba(0,0,0,0.25)', cursor: 'pointer', zIndex: 10000 }}
                >
                  <Plus size={32} />
                </button>
              </div>
            ) : (
             <div className="planning-desktop-wrapper" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', overflowX: 'auto', borderBottom: '1px solid #f1f5f9', marginBottom: 15, scrollbarWidth: 'none' }}>
               <button 
                 onClick={() => setSelectedWorkerIds([])}
                 style={{ padding: '8px 16px', borderRadius: 20, border: '1.5px solid ' + (selectedWorkerIds.length === 0 ? '#0f172a' : '#e2e8f0'), background: selectedWorkerIds.length === 0 ? '#0f172a' : '#fff', color: selectedWorkerIds.length === 0 ? '#fff' : '#64748b', fontSize: 11, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
               >
                 TOUTE L'ÉQUIPE
               </button>
               {workers.map(w => {
                 const isSel = selectedWorkerIds.includes(w.id);
                 const color = getWorkerColor(w);
                 return (
                   <button 
                     key={w.id} 
                     onClick={() => {
                        setSelectedWorkerIds(prev => isSel ? prev.filter(id => id !== w.id) : [...prev, w.id]);
                     }}
                     style={{ 
                       display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 8px', borderRadius: 25, transition: 'all 0.2s', cursor: 'pointer',
                       background: isSel ? color : '#f8fafc', border: '1.5px solid ' + (isSel ? color : '#e2e8f0'), 
                       color: isSel ? '#fff' : '#64748b', boxShadow: isSel ? `0 4px 12px ${color}44` : 'none'
                     }}
                   >
                     <div style={{ width: 20, height: 20, borderRadius: '50%', background: isSel ? '#fff' : color, color: isSel ? color : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900 }}>
                        {(w.name || '?').charAt(0).toUpperCase()}
                     </div>
                     <span style={{ fontSize: 11, fontWeight: 800 }}>{w.name?.split(' ')[0]}</span>
                   </button>
                 );
               })}
            </div>

            <div style={{ display: 'flex', gap: 20, height: '80vh' }}>
                {sidebarOpen && (
                  <div style={{ width: 260, flexShrink: 0, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontWeight: 900, fontSize: 13, color: '#0f172a', marginBottom: 10 }}>🏗️ Chantiers à planifier</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                          <input value={sidebarSearch} onChange={e => setSidebarSearch(e.target.value)} placeholder="Rechercher..." style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 12, fontWeight: 600, boxSizing: 'border-box' }} />
                        </div>
                        <button
                          onClick={() => setSidebarSort(sidebarSort === 'latest' ? 'alpha' : 'latest')}
                          style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#64748b' }}
                          title="Changer l'ordre"
                        >
                          {sidebarSort === 'latest' ? <Clock size={14} /> : <ArrowDownAZ size={14} />}
                        </button>
                      </div>
                    </div>
                    <div ref={externalRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
                      {chantiers
                        .filter(c => {
                          if (c.status === 'archived') return false;
                          return !sidebarSearch ||
                            c.title?.toLowerCase().includes(sidebarSearch.toLowerCase()) ||
                            c.client_name?.toLowerCase().includes(sidebarSearch.toLowerCase());
                        })
                        .sort((a, b) => {
                          if (sidebarSort === 'alpha') return (a.title || '').localeCompare(b.title || '');
                          return (b.created || '').localeCompare(a.created || '');
                        })
                        .map(c => {
                          const chantierAssignments = rawAssignments.filter(a => a.chantier === c.id);
                          const workersInChantier = new Set(chantierAssignments.flatMap(a => Array.isArray(a.user) ? a.user : [a.user]));
                          const workerCount = workersInChantier.size;
                          const assignedHours = chantierAssignments.reduce((acc, a) => {
                            const uCount = Array.isArray(a.user) ? a.user.length : (a.user ? 1 : 0);
                            const h = a.hours_override != null ? parseFloat(a.hours_override) : (calcBusinessHours(a.start_date, a.end_date, businessHours, closures) || 0);
                            return acc + (h * uCount);
                          }, 0);
                          const budget = c.budget_hours || 0;
                          const progress = budget > 0 ? Math.min(100, (assignedHours / budget) * 100) : 0;
                          const isOver = assignedHours > budget && budget > 0;

                          return (
                            <div key={c.id}
                              className="fc-external-event"
                              data-id={c.id}
                              data-title={c.title}
                              data-hours={c.budget_hours || 8}
                              style={{
                                background: '#fff',
                                border: '1.5px solid #e2e8f0',
                                borderLeft: `5px solid ${c.color || '#e2e8f0'}`,
                                borderRadius: 16,
                                padding: '12px',
                                marginBottom: 10,
                                cursor: 'grab',
                                userSelect: 'none',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = c.color || '#86efac'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.05)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <GripVertical size={14} style={{ color: '#cbd5e1', flexShrink: 0, pointerEvents: 'none' }} />
                                <div style={{ flex: 1, minWidth: 0, pointerEvents: 'none' }}>
                                  <div style={{ fontWeight: 900, fontSize: 12, color: c.color || '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{c.client_name || 'Client inconnu'}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button type="button" onMouseDown={e => e.stopPropagation()} onClick={() => { setEditingChantier(c); setIsChantierModalOpen(true); }} style={{ padding: 4, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Edit2 size={12} /></button>
                                </div>
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}><User size={10} /> {workerCount} pers.</div>
                                <div style={{ fontSize: 9, fontWeight: 800, color: isOver ? '#ef4444' : '#0f172a' }}>{Math.round(assignedHours)}h / {budget}h</div>
                              </div>

                              <div style={{ height: 4, background: '#f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{ width: `${progress}%`, height: '100%', background: isOver ? '#ef4444' : (c.color || '#0ea5e9'), transition: 'width 0.3s' }} />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                <div className="calendar-wrapper" style={{ flex: 1, minWidth: 0 }}>
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    locale={frLocale}
                    events={[
                      ...events,
                      ...(() => {
                        const FC_DAY_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
                        if (!businessHours) return [];
                        const closedDays = Object.keys(businessHours)
                          .filter(day => !businessHours[day]?.active)
                          .map(day => FC_DAY_MAP[day]);
                        return closedDays.length === 0 ? [] : [{
                          daysOfWeek: closedDays,
                          display: 'background',
                          color: '#fff1f2',
                          classNames: ['closed-day-bg']
                        }];
                      })(),
                      ...(closures || []).map(c => ({
                        id: `closure-${c.id || Math.random()}`,
                        start: c.date,
                        end: c.date_end ? new Date(new Date(c.date_end).getTime() + 86400000).toISOString().slice(0, 10) : undefined,
                        display: 'background',
                        color: '#fecaca',
                        title: 'BUREAU FERMÉ',
                        classNames: ['exceptional-closure-bg']
                      }))
                    ]}
                    eventContent={renderEventContent}
                    eventDidMount={(arg) => {
                      arg.el.oncontextmenu = (e) => {
                        e.preventDefault();
                        setContextMenu({ show: true, x: e.clientX, y: e.clientY, event: arg.event });
                      };
                    }}
                    eventDragStart={() => setIsDragging(true)}
                    eventDragStop={(arg) => {
                      setIsDragging(false);
                      const y = arg.jsEvent.clientY;
                      if (y > window.innerHeight - 120) {
                        const targetIds = arg.event.extendedProps.rawIds || [arg.event.id];
                        handleDeleteAssignment(targetIds);
                      }
                    }}
                    datesSet={(arg) => setViewType(arg.view.type)}
                    dayHeaderContent={(arg) => {
                      const d = arg.date.toISOString().split('T')[0];
                      const dayName = arg.date.toLocaleString('en-US', { weekday: 'long' }).toLowerCase();
                      const total = dayTotals[d] || 0;
                      const bh = businessHours?.[dayName];
                      const isClosed = bh && !bh.active;

                      return (
                        <div className="custom-day-header" style={{ display: 'flex', flexDirection: 'column', padding: '5px 0' }}>
                          <div className="day-title" style={{ fontWeight: 900, fontSize: 13 }}>{arg.text}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                            {total > 0 && <span style={{ fontSize: 10, fontWeight: 900, color: '#3b82f6', background: '#eff6ff', padding: '2px 6px', borderRadius: 6 }}>{Math.round(total * 10) / 10}h planifiées</span>}
                            {isClosed ? (
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#ef4444' }}>🏢 FERMÉ</span>
                            ) : (
                              bh && <span style={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>🕒 {bh.open} - {bh.close}</span>
                            )}
                          </div>
                        </div>
                      );
                    }}
                    selectable={true}
                    droppable={true}
                    editable={true}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek'
                    }}
                    businessHours={(() => {
                      if (!businessHours) return true;
                      const FC_DAYS = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
                      return Object.keys(businessHours).map(day => {
                        const cfg = businessHours[day];
                        if (cfg?.active) return { daysOfWeek: [FC_DAYS[day]], startTime: cfg.open || '07:30', endTime: cfg.close || '18:30' };
                        return null;
                      }).filter(Boolean);
                    })()}
                    slotMinTime={slotMin}
                    slotMaxTime={slotMax}
                    scrollTime={slotMin}
                    height="100%"
                    eventClick={(info) => {
                      if (isCutterMode) {
                        const rect = info.el.getBoundingClientRect();
                        const pct = (info.jsEvent.clientX - rect.left) / rect.width;
                        const start = info.event.start.getTime();
                        const end = info.event.end.getTime();
                        const splitTime = start + (end - start) * pct;
                        const splitDate = new Date(splitTime).toISOString();

                        // Execute Split precisely at this point
                        (async () => {
                          const raw = info.event.extendedProps.raw;
                          let rawIds = info.event.extendedProps.rawIds || [raw.id];
                          // Remove potential duplicates and ensure we have an array
                          rawIds = [...new Set(rawIds)];
                          if (!raw) return;

                          try {
                            setLoading(true);
                            const rect = info.el.getBoundingClientRect();
                            const pct = (info.jsEvent.clientX - rect.left) / rect.width;
                            const startMs = info.event.start.getTime();
                            const endMs = info.event.end.getTime();
                            const splitMs = startMs + (endMs - startMs) * pct;

                            // ISO string for PB (clean version without MS if possible for better grouping)
                            const splitDate = new Date(splitMs).toISOString().split('.')[0] + 'Z';

                            for (const aid of rawIds) {
                              const a = rawAssignments.find(x => x.id === aid);
                              if (!a) continue;

                              const hA = calcBusinessHours(a.start_date, splitDate, businessHours, closures);
                              const hB = calcBusinessHours(splitDate, a.end_date, businessHours, closures);

                              // Update Part A (Shorten original)
                              await pb.collection('assignments').update(aid, {
                                end_date: splitDate,
                                hours_override: hA || null
                              });
                              // Create Part B (Start of new)
                              await pb.collection('assignments').create({
                                ...a,
                                id: undefined,
                                start_date: splitDate,
                                hours_override: hB || null
                              });
                            }
                            refresh();
                          } catch (e) { alert('Erreur découpe'); }
                          finally { setLoading(false); }
                        })();
                        return;
                      }
                      handleEventClick(info);
                    }}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    select={(s) => {
                      const d = new Date(s.start);
                      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                      const openTime = businessHours?.[dayName]?.active ? businessHours[dayName].open : '08:00';

                      let startStr = s.startStr;
                      if (!startStr.includes('T') || startStr.includes('00:00') || startStr.includes('01:00')) {
                        startStr = s.startStr.slice(0, 10) + 'T' + openTime;
                      }

                      setModalMode('create');
                      setFormData({ id: '', chantier: '', users: [], start_date: startStr.slice(0, 16), end_date: '', hours_override: '', sessions: [] });
                      setIsModalOpen(true);
                    }}
                    eventReceive={(info) => {
                      const chantierId = info.event.extendedProps.chantierId;
                      const h = info.event.extendedProps.hours || 8;

                      const d = new Date(info.event.start);
                      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                      const openTime = businessHours?.[dayName]?.active ? businessHours[dayName].open : '08:00';

                      let startStr = info.event.startStr;
                      if (!startStr.includes('T') || startStr.includes('00:00') || startStr.includes('01:00')) {
                        startStr = info.event.startStr.slice(0, 10) + 'T' + openTime;
                      }

                      const calculatedEnd = addBusinessHours(new Date(startStr), h, businessHours, closures);
                      info.event.remove();
                      setModalMode('create');
                      setFormData({
                        id: '',
                        type: 'mission',
                        chantier: chantierId,
                        users: [],
                        start_date: startStr.slice(0, 16),
                        end_date: calculatedEnd.slice(0, 16),
                        notes: '',
                        hours_override: h,
                        sessions: [],
                        split_budget: false
                      });
                      setIsModalOpen(true);
                    }}
                  />
                </div>
              </div>


            {isChantierModalOpen && (
              <ChantierModal
                item={editingChantier}
                onClose={() => setIsChantierModalOpen(false)}
                onSaved={refresh}
              />
            )}
             </div>
            )}
          </main>

      {/* CONTEXT MENU */}
      {contextMenu.show && (
        <div
          onClick={() => setContextMenu({ ...contextMenu, show: false })}
          style={{ position: 'fixed', inset: 0, zIndex: 29999 }}
        >
          <div style={{
            position: 'fixed', top: contextMenu.y, left: contextMenu.x, width: 180,
            background: '#fff', borderRadius: 16, padding: '8px', zIndex: 30000,
            boxShadow: '0 15px 30px rgba(0,0,0,0.15)', border: '1px solid #f1f5f9',
            animation: 'scaleIn 0.1s ease-out'
          }}>
            <div
              onClick={() => { handleEventClick({ event: contextMenu.event }); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0f172a' }}
              className="ctx-item"
            >
              <Edit2 size={16} /> Modifier
            </div>
            <div
              onClick={() => handleDuplicateAssignment(contextMenu.event)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0f172a' }}
              className="ctx-item"
            >
              <Layers size={16} /> Dupliquer
            </div>
            <div
              onClick={() => { setIsCutterMode(true); setContextMenu({ ...contextMenu, show: false }); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#f43f5e' }}
              className="ctx-item"
            >
              <Scissors size={16} /> Activer le Cutter
            </div>

            {viewType !== 'dayGridMonth' && (
              <div
                onClick={() => { handleDetachDay(contextMenu.event); setContextMenu({ ...contextMenu, show: false }); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0f172a' }}
                className="ctx-item"
              >
                <Calendar size={16} /> Détacher ce jour
              </div>
            )}
            <div
              style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }}
            />
            <div
              onClick={(e) => {
                e.stopPropagation();
                const targetIds = contextMenu.event.extendedProps.rawIds || [contextMenu.event.id];
                handleDeleteAssignment(targetIds);
                setContextMenu({ ...contextMenu, show: false });
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#f43f5e' }}
              className="ctx-item-danger"
            >
              <Trash2 size={16} /> Supprimer
            </div>
          </div>
        </div>
      )}

      {/* TRASH ZONE OVERLAY */}
      {isDragging && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: 120,
          background: 'linear-gradient(to top, #f43f5e, rgba(244, 63, 94, 0.8))',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, color: '#fff', zIndex: 50000, animation: 'slideUp 0.3s ease-out'
        }}>
          <div className="pulse-slow" style={{ background: 'rgba(255,255,255,0.2)', padding: 12, borderRadius: '50%' }}>
            <Trash2 size={32} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>Lâcher ici pour supprimer</span>
        </div>
      )}

      {/* DETAIL CARD MODAL */}
      {isCardOpen && cardData && (
        <div className="planning-modal-overlay" onClick={(e) => e.target.className === 'planning-modal-overlay' && setIsCardOpen(false)} style={{ zIndex: 12000 }}>
          <div className="mission-detail-card scale-in" style={{
            background: '#fff', width: '100%', maxWidth: 440, borderRadius: 28, overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #f1f5f9'
          }}>
            {/* Header with Color */}
            {(() => {
              const isRDV = cardData.extendedProps.type === 'rdv';
              const customColor = isRDV ? '#a855f7' : (cardData.extendedProps.chantier?.color || '#3b82f6');
              return (
                <div style={{ background: customColor, height: 100, position: 'relative' }}>
                  <button
                    onClick={() => setIsCardOpen(false)}
                    style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <X size={18} />
                  </button>
                  <div style={{ position: 'absolute', bottom: -20, left: 24, width: 64, height: 64, borderRadius: 20, background: '#fff', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, color: customColor }}>
                    {isRDV ? <Calendar size={28} /> : <Briefcase size={28} />}
                  </div>
                </div>
              );
            })()}

            <div style={{ padding: '40px 24px 24px 24px' }}>
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>{cardData.title}</h2>
                {cardData.extendedProps.type !== 'rdv' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13 }}>
                    <MapPin size={14} />
                    <span>{cardData.extendedProps.chantier?.location || 'Lieu non défini'}</span>
                  </div>
                )}
                {cardData.extendedProps.type === 'rdv' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a855f7', fontSize: 12, fontWeight: 800 }}>
                    <Calendar size={14} />
                    <span>Rendez-vous Simple</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                <div style={{ background: '#f8fafc', padding: 12, borderRadius: 16, border: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', marginBottom: 6 }}>
                    <Calendar size={12} /> Période
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>
                    {new Date(cardData.start).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    {cardData.start.toDateString() !== cardData.end.toDateString() && ` - ${new Date(cardData.end).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`}
                  </div>
                </div>
                <div style={{ background: '#f8fafc', padding: 12, borderRadius: 16, border: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', marginBottom: 6 }}>
                    <Clock size={12} /> Horaires
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>
                    {cardData.start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {cardData.end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', marginBottom: 12 }}>
                  <Users size={12} /> Participants ({cardData.extendedProps.users.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {cardData.extendedProps.users.map((u, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#eff6ff', borderRadius: 12, border: '1px solid #dbeafe' }}>
                      <div style={{ width: 18, height: 18, borderRadius: 6, background: getWorkerColor(u), color: '#fff', fontSize: 8, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {(u.name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#1e40af' }}>{u.name || u.username}</span>
                    </div>
                  ))}
                </div>
              </div>

              {cardData.extendedProps.raw?.notes && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', marginBottom: 12 }}>
                    <FileText size={12} /> Notes spécifiques
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, background: '#fff9eb', padding: 12, borderRadius: 16, border: '1px solid #fef3c7', whiteSpace: 'pre-wrap' }}>
                    {cardData.extendedProps.raw.notes}
                  </div>
                </div>
              )}

              {cardData.extendedProps.type !== 'rdv' && cardData.extendedProps.chantier?.description && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', marginBottom: 12 }}>
                    <Info size={12} /> Consignes Chantier
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, background: '#f8fafc', padding: 12, borderRadius: 16 }}>
                    {cardData.extendedProps.chantier.description}
                  </div>
                </div>
              )}

              {cardData.extendedProps.type !== 'rdv' && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cardData.extendedProps.chantier?.location)}`}
                    target="_blank" rel="noreferrer"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px', borderRadius: 16, background: '#0f172a', color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 14 }}
                  >
                    <MapPin size={18} /> Itinéraire Maps
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MEMBER PROFILE MODAL */}
      {isMemberModalOpen && selectedMember && (
        <div className="planning-modal-overlay" onClick={() => setIsMemberModalOpen(false)} style={{ zIndex: 13000 }}>
           <div className="scale-in" style={{ background: '#fff', width: '100%', maxWidth: 360, borderRadius: 28, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #f1f5f9' }} onClick={e => e.stopPropagation()}>
              <div style={{ background: getWorkerColor(selectedMember), height: 80, position: 'relative' }}>
                <div style={{ position: 'absolute', bottom: -30, left: '50%', transform: 'translateX(-50%)', width: 70, height: 70, borderRadius: '50%', background: '#fff', border: '5px solid #fff', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, color: getWorkerColor(selectedMember) }}>
                  {(selectedMember.name || selectedMember.username || '?').slice(0, 2).toUpperCase()}
                </div>
              </div>
              <div style={{ padding: '45px 24px 30px', textAlign: 'center' }}>
                 <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0 }}>{selectedMember.name || selectedMember.username}</h2>
                 <p style={{ margin: '4px 0 20px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>{selectedMember.role || 'Salarié'}</p>
                 
                 <div style={{ display: 'grid', gap: 10, marginBottom: 25 }}>
                    <div style={{ background: '#f8fafc', padding: '10px 15px', borderRadius: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                       <div style={{ color: '#94a3b8' }}><Clock size={16} /></div>
                       <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{ fontSize: 9, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>Equipe</div>
                          <div style={{ fontSize: 13, fontWeight: 800 }}>{selectedMember.active_team ? 'Membre Actif' : 'Non assigné à une équipe'}</div>
                       </div>
                    </div>
                 </div>

                 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button onClick={() => window.location.href = `mailto:${selectedMember.email}`} style={{ border: '1.5px solid #e2e8f0', background: '#fff', padding: '12px', borderRadius: 16, fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><FileText size={16} /> Email</button>
                    <button onClick={() => setIsMemberModalOpen(false)} style={{ border: 'none', background: '#0f172a', color: '#fff', padding: '12px', borderRadius: 16, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Fermer</button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {isModalOpen && (
        <div className="planning-modal-overlay" onClick={(e) => e.target.className === 'planning-modal-overlay' && setIsModalOpen(false)}>
          <div className="planning-modal">
            <div className="modal-top">
              <div className="modal-tabs">
                <button className={modalTab === 'details' ? 'active' : ''} onClick={() => setModalTab('details')}>Mission</button>
                {formData.chantier && (
                  <>
                    <button className={modalTab === 'docs' ? 'active' : ''} onClick={() => setModalTab('docs')}>Plans {siteDetails?.attachments?.length > 0 ? `(${siteDetails.attachments.length})` : ''}</button>
                    <button className={modalTab === 'photos' ? 'active' : ''} onClick={() => setModalTab('photos')}>Photos {siteDetails?.gallery?.length > 0 ? `(${siteDetails.gallery.length})` : ''}</button>
                  </>
                )}
              </div>
              <button className="close-btn" onClick={() => setIsModalOpen(false)}><X size={22} /></button>
            </div>

            <div className="modal-scrollable">
              {modalTab === 'details' && (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!formData.users.length) { alert('Veuillez sélectionner au moins un intervenant.'); return; }
                  if (formData.type === 'rdv' && !formData.title) { alert('Veuillez saisir un titre pour le RDV.'); return; }
                  if (formData.type === 'mission' && !formData.chantier && !siteDetails.title) { alert('Veuillez sélectionner ou créer un chantier.'); return; }

                  setLoading(true);
                  try {
                    let finalChantierId = formData.chantier;
                    if (isQuickCreatingChantier && siteDetails.title) {
                      const newC = await pb.collection('chantiers').create({ title: siteDetails.title, status: 'planned' });
                      finalChantierId = newC.id;
                    }

                    // Normalize periods to save
                    const periods = (formData.sessions && formData.sessions.length > 0)
                      ? formData.sessions
                      : [{ start: formData.start_date, end: formData.end_date, hours: formData.hours_override }];

                    for (const p of periods) {
                      const common = {
                        chantier: formData.type === 'mission' ? finalChantierId : null,
                        type: formData.type,
                        title: formData.type === 'rdv' ? formData.title : '',
                        start_date: new Date(p.start).toISOString(),
                        end_date: new Date(p.end || p.start).toISOString(),
                        notes: formData.notes,
                        hours_override: p.hours ? parseFloat(p.hours) : null,
                        coupure_midi: formData.coupure_midi || false,
                        user: formData.users // Send entire array at once! 🥳
                      };

                      if (modalMode === 'create') {
                        await pb.collection('assignments').create(common);
                      } else {
                        // In edit mode: Update the main record
                        await pb.collection('assignments').update(formData.id, common);

                        // AUTO-CLEANUP: If there were other duplicate records in this visual group
                        // (legacy from old one-record-per-user model), delete them now! ✨
                        const allIds = formData.rawIds || [];
                        const duplicates = allIds.filter(id => id !== formData.id);
                        if (duplicates.length > 0) {
                          await Promise.all(duplicates.map(id => pb.collection('assignments').delete(id)));
                        }

                        // Handle sessions if any (the first session updated the main record, others create new ones)
                        if (periods.length > 1) {
                          for (const p of periods.slice(1)) {
                            await pb.collection('assignments').create({
                              ...common,
                              start_date: new Date(p.start).toISOString(),
                              end_date: new Date(p.end || p.start).toISOString(),
                              hours_override: p.hours ? parseFloat(p.hours) : null
                            });
                          }
                        }
                      }
                    }
                    setIsModalOpen(false);
                    refresh();
                  } catch (err) { alert('Erreur sauvegarde.'); }
                  finally { setLoading(false); }
                }} className="plan-form">

                  {/* TYPE SWITCHER */}
                  <div style={{ display: 'flex', background: '#f1f5f9', padding: 4, borderRadius: 16, marginBottom: 20 }}>
                    <button type="button"
                      onClick={() => setFormData({ ...formData, type: 'mission' })}
                      style={{ flex: 1, padding: '10px', borderRadius: 12, border: 'none', background: formData.type === 'mission' ? '#fff' : 'transparent', color: formData.type === 'mission' ? '#0f172a' : '#64748b', fontWeight: 800, fontSize: 13, cursor: 'pointer', boxShadow: formData.type === 'mission' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}>
                      🏗️ Mission Chantier
                    </button>
                    <button type="button"
                      onClick={() => setFormData({ ...formData, type: 'rdv' })}
                      style={{ flex: 1, padding: '10px', borderRadius: 12, border: 'none', background: formData.type === 'rdv' ? '#fff' : 'transparent', color: formData.type === 'rdv' ? '#a855f7' : '#64748b', fontWeight: 800, fontSize: 13, cursor: 'pointer', boxShadow: formData.type === 'rdv' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s' }}>
                      🗓️ Simple RDV
                    </button>
                  </div>

                  {formData.type === 'mission' && formData.chantier && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fef3c7', borderRadius: 20, padding: '16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 15 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 14, background: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Briefcase size={22} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: '#92400e' }}>Suivi Budget Chantier</div>
                        {(() => {
                          const cAssignments = rawAssignments.filter(a => a.chantier === formData.chantier);
                          const totalConsumed = cAssignments.reduce((acc, a) => {
                            const uCount = Array.isArray(a.user) ? a.user.length : (a.user ? 1 : 0);
                            const h = a.hours_override != null ? parseFloat(a.hours_override) : (calcBusinessHours(a.start_date, a.end_date, businessHours, closures) || 0);
                            return acc + (h * uCount);
                          }, 0);

                          const budget = siteDetails?.budget_hours || 0;
                          const remaining = budget - totalConsumed;
                          const workerCount = new Set(cAssignments.flatMap(a => Array.isArray(a.user) ? a.user : [a.user])).size;

                          return (
                            <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700, marginTop: 2 }}>
                              Budget: <strong>{budget}h</strong> | Consommé Total: <strong style={{ color: totalConsumed > budget ? '#ef4444' : '#b45309' }}>{Math.round(totalConsumed * 10) / 10}h</strong> ({workerCount} pers. impliquées) | Reste: <strong>{Math.round(remaining * 10) / 10}h</strong>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {formData.type === 'mission' ? (
                    <div className="field">
                      <label>Chantier de destination</label>
                      <div className="field-row">
                        <>
                          <select required={formData.type === 'mission'} value={formData.chantier} onChange={e => setFormData({ ...formData, chantier: e.target.value })}>
                            <option value="">Sélectionner un chantier...</option>
                            {chantiers.filter(c => c.status !== 'archived').map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                          </select>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              setEditingChantier(null);
                              setIsChantierModalOpen(true);
                            }}
                            title="Nouveau chantier complet"
                          >
                            <Plus size={20} />
                          </button>
                        </>
                      </div>
                    </div>
                  ) : (
                    <div className="field">
                      <label>Objet du Rendez-vous</label>
                      <input
                        placeholder="Ex: Réunion d'équipe, Rdv Commercial..."
                        required={formData.type === 'rdv'}
                        value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                        style={{ padding: '12px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontWeight: 600, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  {/* Travel Info Section (Only for Mission) */}
                  {formData.type === 'mission' && siteDetails?.lat && cfg?.base_lat && (
                    <TravelIndicator
                      lat1={cfg.base_lat} lon1={cfg.base_lon}
                      lat2={siteDetails.lat} lon2={siteDetails.lon}
                      destination={siteDetails.location}
                    />
                  )}

                  <div className="field">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={{ margin: 0 }}>Intervenant(s) / Salarié(s)</label>
                      {formData.type === 'mission' && formData.chantier && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: formData.split_budget ? '#eff6ff' : '#f8fafc', padding: '4px 8px', borderRadius: 8, border: '1.5px solid ' + (formData.split_budget ? '#3b82f6' : '#e2e8f0'), transition: 'all 0.2s' }}>
                          <input
                            type="checkbox"
                            checked={formData.split_budget}
                            onChange={e => {
                              const active = e.target.checked;
                              let nextHours = formData.hours_override;
                              if (active && siteDetails?.budget_hours && formData.users.length > 0) {
                                nextHours = Math.round((siteDetails.budget_hours / formData.users.length) * 10) / 10;
                              }
                              setFormData({ ...formData, split_budget: active, hours_override: nextHours });
                            }}
                            style={{ width: 14, height: 14, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 10, fontWeight: 900, color: formData.split_budget ? '#3b82f6' : '#64748b' }}>⚡ RÉPARTIR BUDGET</span>
                        </label>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 6, background: '#f8fafc', padding: 12, borderRadius: 16, border: '1.5px solid #e2e8f0', maxHeight: 200, overflowY: 'auto' }}>
                      {workers.map(w => {
                        const active = formData.users.includes(w.id);
                        return (
                          <div key={w.id}
                            onClick={() => {
                              const nextUsers = active ? formData.users.filter(id => id !== w.id) : [...formData.users, w.id];
                              let nextHours = formData.hours_override;
                              if (formData.split_budget && siteDetails?.budget_hours && nextUsers.length > 0) {
                                nextHours = Math.round((siteDetails.budget_hours / nextUsers.length) * 10) / 10;
                              }
                              setFormData({ ...formData, users: nextUsers, hours_override: nextHours });
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10,
                              background: active ? '#eff6ff' : '#fff',
                              border: '1px solid ' + (active ? '#3b82f6' : '#f1f5f9'),
                              cursor: 'pointer', transition: 'all 0.2s',
                              transform: active ? 'scale(1.02)' : 'scale(1)',
                              boxShadow: active ? '0 4px 6px -1px rgba(59, 130, 246, 0.1)' : 'none'
                            }}>
                            <div style={{ width: 22, height: 22, borderRadius: 6, background: active ? getWorkerColor(w) : '#f1f5f9', color: active ? '#fff' : '#64748b', fontSize: 9, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {(w.name || w.username || '?').slice(0, 2).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 800, color: active ? '#1d4ed8' : '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name || w.username}</span>
                            {active && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="field">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <label style={{ margin: 0 }}>{formData.type === 'rdv' ? 'Date & Horaire' : "Périodes d'intervention"}</label>
                      {formData.type === 'mission' && modalMode === 'create' && (
                        <button
                          type="button"
                          onClick={() => {
                            const last = formData.sessions?.[formData.sessions.length - 1] || { start: formData.start_date, end: formData.end_date };
                            const nextStart = new Date(new Date(last.end || last.start).getTime() + 86400000).toISOString().slice(0, 16);
                            setFormData({
                              ...formData,
                              sessions: [...(formData.sessions || []), { start: nextStart, end: '', hours: '' }]
                            });
                          }}
                          style={{ background: '#eff6ff', color: '#3b82f6', border: 'none', padding: '6px 12px', borderRadius: 10, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <Plus size={14} /> Ajouter une session
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {formData.type === 'rdv' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                          <MiniCalendar
                            value={formData.start_date.slice(0, 10)}
                            onChange={(d) => {
                              const t1 = formData.start_date.slice(11, 16) || '08:00';
                              const t2 = formData.end_date.slice(11, 16) || '09:00';
                              setFormData({ ...formData, start_date: d + 'T' + t1, end_date: d + 'T' + t2 });
                            }}
                          />

                          <div style={{ background: '#f8fafc', padding: 16, borderRadius: 20, border: '1.5px solid #e2e8f0', display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, fontWeight: 900, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Heure Début</div>
                              <input
                                type="time"
                                value={formData.start_date.slice(11, 16)}
                                onChange={(e) => {
                                  const startVal = e.target.value;
                                  const [h, m] = startVal.split(':').map(Number);
                                  const endH = (h + 1) % 24;
                                  const endVal = String(endH).padStart(2, '0') + ':' + String(m).padStart(2, '0');
                                  setFormData({
                                    ...formData,
                                    start_date: formData.start_date.slice(0, 11) + startVal,
                                    end_date: (formData.end_date || formData.start_date).slice(0, 11) + endVal
                                  });
                                }}
                                style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 15, fontWeight: 800, color: '#0f172a' }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 9, fontWeight: 900, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Heure Fin</div>
                              <input
                                type="time"
                                value={formData.end_date.slice(11, 16)}
                                onChange={(e) => {
                                  setFormData({ ...formData, end_date: (formData.end_date || formData.start_date).slice(0, 11) + e.target.value });
                                }}
                                style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 15, fontWeight: 800, color: '#0f172a' }}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        (!formData.sessions || formData.sessions.length === 0) ? (
                          <div style={{ background: '#f8fafc', padding: 16, borderRadius: 16, border: '1.5px solid #e2e8f0' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                              <div className="field" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>Début</label>
                                <input
                                  type="datetime-local"
                                  value={formData.start_date}
                                  onChange={e => {
                                    const start = e.target.value;
                                    setFormData({ ...formData, start_date: start, hours_override: calcBusinessHours(start, formData.end_date, businessHours, closures) || '' });
                                  }}
                                  style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, width: '100%' }}
                                />
                              </div>
                              <div className="field" style={{ marginBottom: 0 }}>
                                <label style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>Fin</label>
                                <input
                                  type="datetime-local"
                                  value={formData.end_date}
                                  onChange={e => {
                                    const end = e.target.value;
                                    setFormData({ ...formData, end_date: end, hours_override: calcBusinessHours(formData.start_date, end, businessHours, closures) || '' });
                                  }}
                                  style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, width: '100%' }}
                                />
                              </div>
                            </div>
                            <RangeDatePicker
                              startDate={formData.start_date}
                              endDate={formData.end_date}
                              hours={formData.hours_override}
                              businessHours={businessHours}
                              closures={closures}
                              onChange={(start, end) => {
                                const h = calcBusinessHours(start, end, businessHours, closures);
                                setFormData({ ...formData, start_date: start, end_date: end, hours_override: h || '' });
                              }}
                            />
                            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontSize: 11, fontWeight: 900, color: '#94a3b8' }}>⏱ AUTO (H) :</span>
                              <span style={{ fontSize: 14, fontWeight: 900, color: '#0f172a' }}>{calcBusinessHours(formData.start_date, formData.end_date, businessHours, closures) || 0}h</span>
                            </div>
                          </div>
                        ) : (
                          formData.sessions.map((s, idx) => (
                            <div key={idx} style={{ background: '#f8fafc', padding: 16, borderRadius: 16, border: '1.5px solid #e2e8f0', position: 'relative' }}>
                              <div style={{ position: 'absolute', top: -10, left: 16, background: '#3b82f6', color: '#fff', fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 6 }}>SESSION {idx + 1}</div>
                              {idx > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = [...formData.sessions];
                                    next.splice(idx, 1);
                                    setFormData({ ...formData, sessions: next });
                                  }}
                                  style={{ position: 'absolute', top: 12, right: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                  <X size={16} />
                                </button>
                              )}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, marginTop: 10 }}>
                                <div className="field" style={{ marginBottom: 0 }}>
                                  <label style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>Début</label>
                                  <input
                                    type="datetime-local"
                                    value={s.start}
                                    onChange={e => {
                                      const next = [...formData.sessions];
                                      const start = e.target.value;
                                      next[idx] = { ...next[idx], start, hours: calcBusinessHours(start, s.end, businessHours, closures) || '' };
                                      setFormData({ ...formData, sessions: next });
                                    }}
                                    style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, width: '100%' }}
                                  />
                                </div>
                                <div className="field" style={{ marginBottom: 0 }}>
                                  <label style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>Fin</label>
                                  <input
                                    type="datetime-local"
                                    value={s.end}
                                    onChange={e => {
                                      const next = [...formData.sessions];
                                      const end = e.target.value;
                                      next[idx] = { ...next[idx], end, hours: calcBusinessHours(s.start, end, businessHours, closures) || '' };
                                      setFormData({ ...formData, sessions: next });
                                    }}
                                    style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 700, width: '100%' }}
                                  />
                                </div>
                              </div>
                              <RangeDatePicker
                                startDate={s.start}
                                endDate={s.end}
                                hours={s.hours}
                                businessHours={businessHours}
                                closures={closures}
                                onChange={(start, end) => {
                                  const next = [...formData.sessions];
                                  const h = calcBusinessHours(start, end, businessHours, closures);
                                  next[idx] = { ...next[idx], start, end, hours: h || '' };
                                  setFormData({ ...formData, sessions: next });
                                }}
                              />
                              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 11, fontWeight: 900, color: '#94a3b8' }}>⏱ AUTO (H) :</span>
                                <span style={{ fontSize: 14, fontWeight: 900, color: '#0f172a' }}>{calcBusinessHours(s.start, s.end, businessHours, closures) || 0}h</span>
                              </div>
                            </div>
                          ))
                        )
                      )}
                    </div>
                  </div>

                  <div className="field">
                    <label>Consignes particulières</label>
                    <textarea value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Détails de la mission..." />
                  </div>

                  {/* Heures auto + override (Only for Mission) */}
                  {formData.type === 'mission' && (() => {
                    const autoH = calcBusinessHours(formData.start_date, formData.end_date, businessHours, closures);
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: 16, padding: '14px 18px', border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <div style={{ fontSize: 11, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>⏱ Heures estimées (auto)</div>
                          {autoH !== null ?
                            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>{autoH}<span style={{ fontSize: 13, color: '#64748b', marginLeft: 4 }}>h</span></div> :
                            <div style={{ fontSize: 13, color: '#cbd5e1', fontWeight: 700 }}>Sélectionnez les dates</div>
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <label style={{ fontSize: 11, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Override manuel (h)</label>
                          <input
                            type="number" min="0" step="0.5"
                            placeholder={autoH !== null ? `${autoH}h (auto)` : 'ex: 24'}
                            value={formData.hours_override}
                            onChange={e => {
                              const val = e.target.value;
                              let nextEnd = formData.end_date;
                              if (val && formData.start_date) {
                                const h = parseFloat(val);
                                if (!isNaN(h)) {
                                  nextEnd = addBusinessHours(formData.start_date, h, businessHours, closures);
                                }
                              }
                              setFormData({ ...formData, hours_override: val, end_date: nextEnd });
                            }}
                            style={{ padding: '10px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontWeight: 800, fontSize: 15, width: '100%', boxSizing: 'border-box' }}
                          />
                          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input 
                              type="checkbox" 
                              id="coupure_midi" 
                              checked={formData.coupure_midi} 
                              onChange={(e) => {
                                const checked = e.target.checked;
                                let newOverride = formData.hours_override;
                                const workingDays = calcWorkingDays(formData.start_date, formData.end_date, businessHours, closures) || 1;
                                if (checked) {
                                  newOverride = formData.hours_override ? Math.max(0, parseFloat(formData.hours_override) - workingDays) : (autoH !== null ? Math.max(0, autoH - workingDays) : '');
                                } else {
                                  newOverride = formData.hours_override ? parseFloat(formData.hours_override) + workingDays : '';
                                  if (autoH !== null && newOverride === autoH) newOverride = ''; 
                                }
                                setFormData({ ...formData, coupure_midi: checked, hours_override: newOverride });
                              }} 
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <label htmlFor="coupure_midi" style={{ fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer' }}>
                              Coupure midi (-1h/jour)
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="modal-actions">
                    {modalMode === 'edit' && (
                      <button type="button" onClick={() => handleDeleteAssignment(formData.id)} className="btn-delete-plan"><Trash2 size={18} /></button>
                    )}
                    <div className="spacer"></div>
                    <button type="submit" className="btn-submit-plan">
                      {loading ? 'Traitement...' : <><Save size={18} /> Valider le Planning</>}
                    </button>
                  </div>
                </form>
              )}

              {(modalTab === 'docs' || modalTab === 'photos') && (
                <div className="modal-files-area">
                  {uploadError && <div className="p-error">{uploadError}</div>}
                  <div className="p-upload-zone" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <label className="p-upload-btn">
                        {modalTab === 'docs' ? <FileText size={20} /> : <Upload size={20} />}
                        <span>{modalTab === 'docs' ? 'PDF (Lots)' : 'Photos'}</span>
                        <input type="file" multiple accept={modalTab === 'docs' ? '.pdf' : 'image/*'} onChange={e => handleFileUpload(e, modalTab === 'docs' ? 'attachments' : 'gallery')} hidden />
                      </label>
                      {modalTab === 'docs' && (
                        <label className="p-upload-btn" style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}>
                          <Camera size={20} />
                          <span>Photo Plan</span>
                          <input type="file" accept="image/*" capture="environment" onChange={e => handleFileUpload(e, 'attachments')} hidden />
                        </label>
                      )}
                    </div>
                    <p className="p-upload-sub">Cliquez pour ajouter des éléments au dossier</p>
                  </div>

                  <div className="p-files-grid">
                    {(modalTab === 'docs' ? siteDetails.attachments : siteDetails.gallery || []).map(f => (
                      <div key={f} className="p-file-card">
                        <div className="p-file-preview">
                          {modalTab === 'photos' || f.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                            <img src={getFileUrl(siteDetails, f)} alt="Prévu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div className="pdf-icon"><FileText size={24} /></div>
                          )}
                        </div>
                        <div className="p-file-info">
                          <span title={f}>{f.split('_').pop().slice(-15)}</span>
                          <div className="p-file-actions">
                            <a href={getFileUrl(siteDetails, f)} target="_blank" rel="noreferrer" title="Voir"><Eye size={16} /></a>
                            <button onClick={() => handleRemoveFile(f, modalTab === 'docs' ? 'attachments' : 'gallery')} title="Supprimer"><Trash2 size={16} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


    <style>{`
        .skeleton-container { display: flex; flex-direction: column; gap: 20px; padding: 20px 0; }
        .skeleton-header { height: 80px; background: #f1f5f9; border-radius: 24px; animation: pulse 1.5s infinite; }
        .skeleton-body { display: flex; gap: 20px; flex: 1; height: 60vh; }
        .skeleton-sidebar { width: 260px; background: #f8fafc; border-radius: 24px; animation: pulse 1.5s infinite; }
        .skeleton-calendar { flex: 1; background: #fff; border: 1px solid #f1f5f9; border-radius: 24px; animation: pulse 1.5s infinite; }
        @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }

        .planning-page { min-height: 100%; display: flex; flex-direction: column; gap: 25px; animation: fadeIn 0.4s ease; }
        
        .planning-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .planning-header h1 { font-size: 28px; font-weight: 900; margin: 0 0 5px 0; color: #0f172a; }
        .planning-header .muted { margin: 0; }
        
        .btn-add-main { 
          background: var(--admin-primary); border: none; color: #000; border-radius: 16px; 
          padding: 12px 24px; font-weight: 800; display: flex; align-items: center; gap: 10px;
          cursor: pointer; box-shadow: 0 10px 15px -3px rgba(234, 179, 8, 0.2); transition: all 0.2s;
        }
        .btn-add-main:hover { transform: translateY(-2px); box-shadow: 0 20px 25px -5px rgba(234, 179, 8, 0.3); }

        .planning-main { flex: 1; }
        
        /* CALENDAR THEME */
        .calendar-wrapper { 
          background: #fff; border-radius: 28px; border: 1px solid var(--admin-border); padding: 25px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.02);
        }
        .fc { --fc-border-color: #f1f5f9; --fc-button-bg-color: #fff; --fc-button-border-color: #e2e8f0; --fc-button-text-color: #0f172a; --fc-button-hover-bg-color: #f8fafc; --fc-button-active-bg-color: var(--admin-primary); --fc-button-active-border-color: var(--admin-primary); }
        .fc-toolbar-title { font-weight: 900 !important; color: #0f172a !important; font-size: 20px !important; }
        .fc .fc-button { border-radius: 12px !important; font-weight: 700 !important; font-size: 14px !important; padding: 8px 16px !important; }
        .fc-event { border: none !important; border-radius: 8px !important; padding: 2px 4px !important; }
        
        /* Pearl Styles for Business Hours & Today */
        .fc-non-business { background: rgba(241, 245, 249, 0.4) !important; }
        
        /* Fixed Closures (Weekends) */
        .closed-day-bg { background-color: #fff1f2 !important; border-top: 2px solid #fecaca; }
        .closed-day-bg::before { content: 'W-E / REPOS'; position: absolute; top: 35px; left: 5px; font-size: 8px; font-weight: 900; color: #f43f5e; opacity: 0.5; text-transform: uppercase; }

        /* Exceptional Closures */
        .exceptional-closure-bg { background-color: #fee2e2 !important; border: 1.5px dashed #f87171; }
        .exceptional-closure-bg::before { content: 'FERMÉ'; position: absolute; top: 45px; left: 5px; font-size: 9px; font-weight: 900; color: #dc2626; text-transform: uppercase; }

        .fc-day-today { background: #fffdf5 !important; border: 2px solid var(--admin-primary) !important; }
        .unified-event-card { 
          padding: 2px 4px; border-radius: 8px; border-left: 8px solid #cbd5e1; 
          background: #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.05); color: #0f172a; 
          height: 24px; min-height: 24px;
        }
        .unified-title { font-size: 10px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1; }
        .worker-rainbow-strip { display: flex; gap: 2px; height: 6px; margin-top: 2px; border-radius: 2px; overflow: hidden; }
        .worker-dot { flex: 1; height: 100%; border-radius: 1px; }

        .striped-inactive {
          background: repeating-linear-gradient(
            -45deg,
            #fafafa,
            #fafafa 5px,
            #f5f5f5 5px,
            #f5f5f5 10px
          ) !important;
          border-left: 8px solid #e5e7eb !important;
          opacity: 0.8;
          box-shadow: none !important;
        }

        /* Seamless connection for grouped segments */
        .fc-h-event { border: none !important; margin-bottom: 2px !important; }
        .fc-event[data-group-id] { border-radius: 0 !important; margin-left: 0 !important; margin-right: 0 !important; }

        .day-total-pill {
          background: #eff6ff; 
          color: #1e40af; 
          font-size: 9px; 
          font-weight: 900; 
          padding: 2px 6px; 
          border-radius: 20px; 
          border: 1px solid #bfdbfe;
          white-space: nowrap;
        }
        .day-total-pill.header-mode { align-self: center; background: #fff; transform: scale(0.9); }

        /* MOBILE TIMELINE */
        .mobile-timeline { display: grid; gap: 12px; }
        .mobile-plan-card { 
          background: #fff; border: 1px solid var(--admin-border); border-radius: 20px; padding: 18px;
          display: flex; align-items: center; gap: 15px; position: relative; transition: all 0.2s;
        }
        .mobile-plan-card:active { transform: scale(0.98); background: #f8fafc; }
        .plan-time { width: 65px; font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase; display: flex; flex-direction: column; gap: 4px; align-items: start; }
        .plan-body { flex: 1; }
        .plan-body h3 { font-size: 15px; font-weight: 800; margin: 0 0 4px 0; color: #0f172a; }
        .plan-worker { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 600; }
        .plan-arrow { color: var(--admin-border); }

        /* MODAL SIGNATURE */
        .planning-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(8px); z-index: 20000; display: flex; align-items: center; justify-content: center; }
        .planning-modal { background: #fff; width: 100%; max-width: 650px; border-radius: 32px; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .modal-top { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .modal-tabs { display: flex; gap: 10px; background: #f1f5f9; padding: 4px; border-radius: 14px; }
        .modal-tabs button { padding: 8px 20px; border: none; background: none; font-weight: 800; font-size: 13px; cursor: pointer; border-radius: 10px; color: #64748b; transition: all 0.2s; }
        .modal-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
        .close-btn { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f8fafc; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; }

        .modal-scrollable { padding: 24px; overflow-y: auto; }
        .plan-form { display: grid; gap: 20px; }
        .field { display: grid; gap: 8px; }
        .field label { font-size: 12px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin-left: 2px; }
        .field input, .field select, .field textarea { background: #f8fafc; border: 1px solid #e2e8f0; padding: 14px 18px; border-radius: 16px; font-size: 14px; font-weight: 600; color: #0f172a; transition: all 0.2s; }
        .field input:focus, .field select:focus { border-color: var(--admin-primary); outline: none; background: #fff; box-shadow: 0 0 0 4px var(--admin-primary-soft); }
        .field-row { display: flex; gap: 10px; }
        .btn-icon { width: 50px; border-radius: 16px; border: 1px solid #e2e8f0; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--admin-primary); }
        .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }

        .modal-actions { display: flex; align-items: center; gap: 15px; margin-top: 20px; }
        .btn-delete-plan { width: 50px; height: 50px; border-radius: 16px; background: #fff1f2; color: #f43f5e; border: 1px solid #ffe4e6; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .btn-submit-plan { flex: 1; height: 52px; background: #0f172a; color: #fff; border: none; border-radius: 16px; font-weight: 800; font-size: 15px; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer; transition: all 0.2s; }
        .btn-submit-plan:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.15); }

        /* UPLOAD AREA */
        .p-upload-zone { border: 2px dashed #e2e8f0; border-radius: 24px; padding: 30px; text-align: center; background: #f8fafc; transition: all 0.2s; }
        .p-upload-btn { display: inline-flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #e2e8f0; padding: 12px 24px; border-radius: 14px; font-weight: 700; cursor: pointer; margin-bottom: 8px; }
        .p-upload-sub { font-size: 13px; color: #94a3b8; margin: 0; }
        .p-files-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 15px; margin-top: 25px; }
        .p-file-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; overflow: hidden; display: flex; flex-direction: column; }
        .p-file-preview { height: 100px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .p-file-preview img { width: 100%; height: 100%; object-fit: cover; }
        .p-file-info { padding: 10px; display: flex; justify-content: space-between; align-items: center; }
        .p-file-info span { font-size: 11px; font-weight: 700; color: #64748b; }
        .p-file-actions { display: flex; gap: 5px; }
        .p-file-actions button, .p-file-actions a { width: 28px; height: 28px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #64748b; cursor: pointer; }
        .p-file-actions button:hover { background: #fee2e2; color: #f43f5e; }

        @media (max-width: 900px) {
          .planning-header { align-items: flex-start; }
          .planning-header h1 { font-size: 24px; }
          .btn-add-main { padding: 10px 18px; border-radius: 14px; }
          .planning-modal { border-radius: 0; height: 100vh; max-height: 100vh; }
          .modal-top { padding-top: 40px; } /* Safe area */
          .field-grid { grid-template-columns: 1fr; }
        }
        
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes pulse-slow { 0% { transform: scale(1); } 50% { transform: scale(1.1); } 100% { transform: scale(1); } }
        .pulse-slow { animation: pulse-slow 2s infinite ease-in-out; }

        @keyframes pulse-red { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }
        .cutter-active { animation: pulse-red 1.5s infinite; border-color: #ef4444 !important; }

        .ctx-item { transition: all 0.2s; }
        .ctx-item:hover { background: #f1f5f9; color: var(--admin-primary) !important; }
        .ctx-item-danger:hover { background: #fee2e2; color: #ef4444 !important; }

        .cutter-mode-active .fc-event { cursor: crosshair !important; }
        .cutter-mode-active .fc-event:hover { filter: brightness(1.1); transform: scale(1.01); outline: 2px dashed #ef4444; }
      `}</style>
     </>
    )}
    </div>
  );
}
